<script setup>
/* eslint-disable */
import { onMounted, onUnmounted, watch, ref, computed } from 'vue';
import { useTimedSportStore } from '@/stores/timed_sport';
import PreFixtureDoubleMarket from './PreFixtureDoubleMarket.vue';
import LiveFixtureDoubleMarket from './LiveFixtureDoubleMarket.vue';
import FixtureSkeletonLoader from './FixtureSkeletonLoader.vue';
import { useSocketStore } from '@/stores/socket';
import OutrightFixture from './OutrightFixture.vue';
import _ from 'lodash';
import { usePreSportsStore } from '@/stores/pre_sports';

const timedSportStore = useTimedSportStore();
const preSportsStore = usePreSportsStore();

const sportName = computed(() => {
	if (props.sport_id && preSportsStore.preSportName) {
		return preSportsStore.preSportName(props.sport_id);
	}
	return '';
});

const sportIcon = computed(() => {
	if (props.sport_id && preSportsStore.preSportWhiteIcon) {
		return preSportsStore.preSportWhiteIcon(props.sport_id);
	}
	return null;
});

const props = defineProps({
	primary_overview_market: {
		type: Object,
		required: true,
	},
	secondary_overview_market: {
		type: Object,
		required: true,
	},
	time: {
		type: String,
		default: 'today',
	},
	sport_id: {
		type: Number,
		default: 1,
	},
	competition_ids: {
		type: [Array],
		default: [],
	},
	pre_only: {
		type: Boolean,
		default: false,
	},
});

const loading = ref(false);

const sortedFixtures = computed(() => {
	const fixtures = timedSportStore.time_fixtures || [];
	return _.orderBy(fixtures, ['start_datetime'], ['asc']);
});

const fetchFixtures = () => {
	loading.value = true;
	console.log('fetchings', props.time, props.competition_ids, props.sport_id);
	timedSportStore.fetchTimeFixtures(props.sport_id, props.time, false, props.competition_ids, props.pre_only).then(() => {
		loading.value = false;
	}).catch(() => {
		loading.value = false;
	});
};

const loadMore = () => {
	if (!timedSportStore.is_loading && timedSportStore.current_page < timedSportStore.last_page) {
		timedSportStore.loadMoreFixtures(props.sport_id, props.time, props.competition_ids, props.pre_only);
	}
};

const visibilityChanged = (isVisible) => {
	if (isVisible && !loading.value) {
		loadMore();
	}
};

const handleScroll = () => {
	if (loading.value || timedSportStore.is_loading || timedSportStore.current_page >= timedSportStore.last_page) {
		return;
	}
	const scrollPosition = window.innerHeight + window.scrollY;
	const threshold = document.documentElement.scrollHeight - 350;
	if (scrollPosition >= threshold) {
		loadMore();
	}
};

watch([() => props.sport_id, () => props.time], () => {
	fetchFixtures();
});

onMounted(() => {

	fetchFixtures();
	window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
	timedSportStore.leaveTimeFixtures();
})


onUnmounted(() => {
	window.removeEventListener('scroll', handleScroll);
});
</script>

<template>
	<div id="by-time" class="tab-content">
		<div v-if="loading && (!timedSportStore.time_fixtures || timedSportStore.time_fixtures.length === 0)">
			<FixtureSkeletonLoader :rows="5" />
		</div>

		<template v-else-if="sortedFixtures.length > 0">
			<div class="live-info-outer">
				<div class="live-info-inner" v-if="!timedSportStore.isAllTimeFixturesOutrights">
					<div class="live-info-team">
						<div class="live-match time-sport-header">
							<div class="team-flag" v-if="sportIcon">
								<svg class="normal-img" xmlns="http://www.w3.org/2000/svg" style="width:16px; height: 16px; stroke: var(--white); fill: none;" viewBox="0 0 30 25" xmlns:xlink="http://www.w3.org/1999/xlink">
									<use :xlink:href="sportIcon + '#svg3108'" stroke-width="1.2px" />
								</svg>
							</div>
							<div class="live-match-name">
								<h3 class="sport-time-title">{{ sportName }}</h3>
							</div>
						</div>
					</div>

					<div class="full-time-top top-title-dash" :class="{'totals_new-grid': primary_overview_market?.market_type == 'totals_new', 'handicap_new_grid': primary_overview_market?.market_type == 'handicap_new'}">
						<span v-if="primary_overview_market?.market_type == 'totals_new' || primary_overview_market?.market_type == 'handicap_new'"/>
						<template v-for="(odd, id) in primary_overview_market?.odd_names" :key="id">
							<span v-if="odd?.length >= 1">{{ odd }}</span>
						</template>
					</div>

					<div class="match-goal-top top-title-dash" :class="{'totals_new-grid': secondary_overview_market?.market_type == 'totals_new', 'handicap_new_grid': secondary_overview_market?.market_type == 'handicap_new'}">
						<span v-if="secondary_overview_market?.market_type == 'totals_new' || secondary_overview_market?.market_type == 'handicap_new'"/>
						<template v-for="(odd, id) in secondary_overview_market?.odd_names" :key="id">
							<span v-if="odd?.length >= 1">{{ odd }}</span>
						</template>
						<span class="bet-column-empty"></span>
					</div>
				</div>

				<template v-for="(fixture, fIndex) in sortedFixtures" :key="fixture.id || fIndex">
					<template v-if="!fixture.outright">
						<LiveFixtureDoubleMarket v-if="fixture.live"
							:primary_overview_market="primary_overview_market"
							:secondary_overview_market="secondary_overview_market"
							:fixture="fixture"
							:show_competition_region="true"
						/>

						<PreFixtureDoubleMarket v-if="!fixture.live"
							:primary_overview_market="primary_overview_market"
							:secondary_overview_market="secondary_overview_market"
							:fixture="fixture"
							:show_competition_region="true"
						/>
					</template>
					<template v-else-if="fixture.outright">
						<OutrightFixture
							:primary_overview_market="primary_overview_market"
							:secondary_overview_market="secondary_overview_market"
							:fixture="fixture"
							:show_competition_region="true"
						/>
					</template>
				</template>
			</div>

			<!-- Sentinel element & loader for infinite scroll -->
			<div v-if="timedSportStore.current_page < timedSportStore.last_page" v-observe-visibility="visibilityChanged" class="scroll-sentinel">
				<FixtureSkeletonLoader v-if="timedSportStore.is_loading" :rows="5" />
			</div>
		</template>

		<div v-else class="empty-state">
			<div class="empty-state-icon">
				<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
					<circle cx="12" cy="12" r="10"/>
					<line x1="12" y1="8" x2="12" y2="12"/>
					<line x1="12" y1="16" x2="12.01" y2="16"/>
				</svg>
			</div>
			<h3 class="empty-state-title">{{ $t('general.no_fixtures_available', 'No fixtures available') }}</h3>
			<p class="empty-state-subtitle">{{ $t('general.no_fixtures_message', 'There are no upcoming events for the selected time period. Try adjusting your filters or check back later.') }}</p>
		</div>
	</div>
</template>

<style scoped>
.time-sport-header {
	display: flex !important;
	align-items: center !important;
	gap: 8px !important;
}

.sport-time-title {
	font-size: 13px !important;
	font-weight: 700 !important;
	color: var(--white) !important;
	margin: 0 !important;
	text-transform: uppercase;
}

.scroll-sentinel {
	min-height: 40px;
	padding: 10px 0;
}

.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 24px;
	text-align: center;
	opacity: 0;
	animation: fadeIn 0.4s ease forwards;
}

@keyframes fadeIn {
	to { opacity: 1; }
}

.empty-state-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 80px;
	height: 80px;
	border-radius: 50%;
	background: rgba(var(--primary-rgb, 99, 162, 70), 0.08);
	color: var(--primary, #63A246);
	margin-bottom: 20px;
}

.empty-state-title {
	font-size: 1.15rem;
	font-weight: 600;
	color: var(--white, #fff);
	margin: 0 0 8px 0;
}

.empty-state-subtitle {
	font-size: 0.875rem;
	color: var(--light-gray, #8a8f98);
	margin: 0;
	max-width: 360px;
	line-height: 1.5;
}
</style>
